import React, { Suspense, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { flag } from "../config";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsentBanner from "../components/ConsentBanner";
import MobileBottomNav from "../components/MobileBottomNav";
import { PageLoading, SiteChromeContext } from "./PageStates";
import { EnquiryProvider } from "../components/site/enquiry";

/** The id of the canvas's content wrapper (around the page): the skip link's fallback target. */
export const SITE_CONTENT_ID = "site-content";

/**
 * Moves focus to `el` so the next Tab continues inside it. An element that is not focusable
 * gets tabindex="-1" for as long as it holds focus (removed on blur), so later clicks on
 * the page do not focus it; an element with its own tabindex keeps it.
 */
function focusRegion(el) {
  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
    el.addEventListener("blur", () => el.removeAttribute("tabindex"), { once: true });
  }
  el.focus({ preventScroll: true });
  if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
}

/**
 * "Skip to content": the first focusable element of the page, off screen until it has
 * focus (the package's `.skip`: a sheet chip at the top left). It never navigates (the
 * URL keeps no "#", so the router, ScrollToTop and analytics see nothing): focus moves to
 * the page's <main> (pages own their one <main>), or — for the few pages without one —
 * to the canvas's content wrapper (#site-content). Either way the next Tab goes into the
 * page. index.css keeps a focused `main[tabindex="-1"]` free of a ring; the wrapper has
 * `outline-none`. `href="#site-content"` is the no-JavaScript fallback.
 */
export function SkipLink({ targetRef }) {
  const onClick = (event) => {
    event.preventDefault();
    const root = (targetRef && targetRef.current) || document.getElementById(SITE_CONTENT_ID);
    const target = (root && root.querySelector("main")) || root;
    if (target) focusRegion(target);
  };
  return (
    <a
      href={`#${SITE_CONTENT_ID}`}
      onClick={onClick}
      className="site-light fixed left-[15px] top-[-100px] z-[100] border border-navy bg-brand-sheet p-[15px] font-body text-sm font-semibold text-navy shadow-site focus:top-[15px]"
    >
      Skip to content
    </a>
  );
}

/**
 * Site chrome for every patron-facing page: Header (remounted per path, as
 * before), the page, Footer, the cookie ConsentBanner and the patron
 * MobileBottomNav. A route gets this chrome by being declared under
 * <Route element={<PublicLayout />}> (see App.js) — never by a path list.
 *
 * The Suspense boundary around the page (Phase 5c) covers the SLOT_MODEL pages that
 * routes/public.jsx loads on demand: the chrome stays while PageLoading shows.
 * Eagerly imported pages never suspend, so for them nothing changes.
 *
 * SLOT_MODEL on (Sept 2026 redesign): the page sits on the paper canvas — the paper +
 * 24px grid (`.site-paper`) across the full window (owner, 2026-09-28: the site is 100%
 * wide; content stops at 1680px inside SiteContainer / `max-w-canvas`, never here). The
 * page and the footer grow to at least
 * the viewport height; the page sits in #site-content. A "Skip to content" link comes
 * first. Pages keep their own <main> and their own top offset under the fixed header.
 * SLOT_MODEL off: exactly the fragment it always was.
 *
 * Both states provide SiteChromeContext (no markup) so shared pages that render with
 * and without the chrome (NotFound, the page states) know to clear the fixed header.
 */
export default function PublicLayout() {
  const { pathname } = useLocation();
  const contentRef = useRef(null);

  if (!flag("SLOT_MODEL")) {
    return (
      <SiteChromeContext.Provider value>
        <Header key={pathname} />
        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
        <Footer />
        <ConsentBanner />
        <MobileBottomNav />
      </SiteChromeContext.Provider>
    );
  }

  // EnquiryProvider: the one enquiry dialog every redesigned page opens (EnquiryButton,
  // useEnquiry); it renders nothing until opened (a portal), so the markup is unchanged.
  return (
    <SiteChromeContext.Provider value>
      <EnquiryProvider>
        <div className="min-h-screen" data-site-layout="">
          <SkipLink targetRef={contentRef} />
          <Header key={pathname} />
          <div
            className="site-paper flex min-h-screen w-full flex-col"
            data-site-canvas=""
          >
            <div ref={contentRef} id={SITE_CONTENT_ID} className="flex-1 outline-none">
              <Suspense fallback={<PageLoading />}>
                <Outlet />
              </Suspense>
            </div>
            <Footer />
          </div>
          <ConsentBanner />
          <MobileBottomNav />
        </div>
      </EnquiryProvider>
    </SiteChromeContext.Provider>
  );
}
